import { Button } from "@/components/ui/button";
import { ArrowLeft, Home, Search } from "lucide-react";

export default function NotFoundPage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-white p-6 text-center overflow-hidden">
      <div className="relative space-y-8 max-w-lg">
        {/* Decorative elements with enhanced animations */}
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animate-bounce-slow"></div>
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse delay-1000 animate-bounce-slow"></div>
        
        {/* New floating elements */}
        <div className="absolute top-10 right-10 w-8 h-8 bg-yellow-100 rounded-full opacity-60 animate-float-delayed"></div>
        <div className="absolute bottom-32 left-10 w-6 h-6 bg-green-100 rounded-full opacity-50 animate-float"></div>
        <div className="absolute top-32 left-32 w-4 h-4 bg-pink-100 rounded-full opacity-40 animate-float-slow"></div>

        {/* Main content */}
        <div className="relative space-y-6 animate-fade-in-up">
          {/* Animated number with bounce */}
          <div className="relative inline-block animate-bounce-in">
            <h1 className="text-9xl max-md:text-8xl  font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700 tracking-tighter">
              404
            </h1>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer"></div>
          </div>

          {/* Illustration/Icon with rotation */}
          <div className="flex justify-center">
            <div className="relative animate-float-rotate">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center border-2 border-gray-200 shadow-lg">
                <div className="text-4xl animate-wiggle">🔍</div>
              </div>
              <div className="absolute -top-2 -right-2 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center border-2 border-white animate-ping-slow">
                <span className="text-red-600 font-bold text-lg animate-pulse">?</span>
              </div>
            </div>
          </div>

          {/* Text content with staggered animation */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight animate-slide-in-left">
              Page Not Found
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-md mx-auto animate-slide-in-right delay-100">
              Oops! The page you're looking for seems to have wandered off into
              the digital void.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 animate-fade-in delay-200">
              <Search className="w-4 h-4 animate-pulse" />
              <span>Maybe try one of these options below</span>
            </div>
          </div>

          {/* Action buttons with hover animations */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in-up delay-300">
            <Button
              asChild
              className="main-button text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 animate-pulse-once hover:scale-105 active:scale-95"
            >
              <a href="/" className="flex items-center gap-2">
                <Home className="w-5 h-5" />
                Return Home
              </a>
            </Button>
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="px-8 py-6 text-lg font-semibold border-2 hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 hover:scale-105 active:scale-95 hover:translate-x-1"
            >
              <ArrowLeft className="w-5 h-5 mr-2 animate-bounce-horizontal" />
              Go Back
            </Button>
          </div>
        </div>
      </div>
      
      {/* Add these Tailwind animation definitions in your global CSS or at the component level */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes float-rotate {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(5px) rotate(-5deg); }
        }
        @keyframes bounce-in {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-30px); }
        }
        @keyframes slide-in-left {
          from { transform: translateX(-100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slide-in-right {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fade-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(5deg); }
          75% { transform: rotate(-5deg); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes bounce-horizontal {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-5px); }
        }
        @keyframes pulse-once {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 4s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 5s ease-in-out infinite;
        }
        .animate-float-rotate {
          animation: float-rotate 6s ease-in-out infinite;
        }
        .animate-bounce-in {
          animation: bounce-in 1s ease-out;
        }
        .animate-bounce-slow {
          animation: bounce-slow 6s ease-in-out infinite;
        }
        .animate-slide-in-left {
          animation: slide-in-left 0.8s ease-out;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.8s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-wiggle {
          animation: wiggle 2s ease-in-out infinite;
        }
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        .animate-bounce-horizontal {
          animation: bounce-horizontal 2s ease-in-out infinite;
        }
        .animate-pulse-once {
          animation: pulse-once 2s ease-in-out;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}